import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { LanguageSwitcher } from "@/components/layout/LanguageSwitcher";
import { formatApiErrorDetail } from "@/lib/api";
import { APP_NAME } from "@/lib/brand";

const BG_IMG =
  "https://images.unsplash.com/photo-1743657166981-8d8e11d03c3e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwbWVkaWNhbCUyMGFic3RyYWN0JTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3ODI2Njk2ODV8MA&ixlib=rb-4.1.0&q=85";

export default function LoginPage() {
  const { login, register, user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("member");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (user && typeof user === "object") {
      const target = location.state?.from?.pathname || homeFor(user.role);
      navigate(target, { replace: true });
    }
  }, [user, navigate, location.state]);

  function homeFor(r) {
    if (r === "clinician") return "/clinician";
    if (r === "administrator") return "/admin";
    return "/";
  }

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const u =
        mode === "login"
          ? await login(email, password)
          : await register({ email, password, name, role, language: "tr" });
      navigate(homeFor(u.role), { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-[var(--mios-bg)]" data-testid="login-page">
      <div className="grid min-h-screen lg:grid-cols-[1.05fr_1fr]">
        {/* Left: brand */}
        <div
          className="relative hidden lg:flex flex-col justify-between px-12 py-10 text-slate-900"
          style={{
            backgroundImage: `linear-gradient(180deg, rgba(248,250,252,0.7), rgba(248,250,252,0.55)), url(${BG_IMG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-900 text-white">
              <span className="font-semibold tracking-tight">Mi</span>
            </div>
            <div>
              <div className="text-lg font-semibold tracking-tight">{APP_NAME}</div>
              <div className="text-[11px] uppercase tracking-[0.16em] text-slate-600">
                {t.brand.tagline}
              </div>
            </div>
          </div>
          <div className="max-w-md">
            <h1 className="text-4xl font-semibold leading-tight tracking-tight">
              {t.auth.welcome}
            </h1>
            <p className="mt-3 text-base text-slate-700">{t.auth.subtitle}</p>
            <div className="mt-8 grid grid-cols-2 gap-3 text-xs">
              <Pillar code="Mi" title={t.layer.mi} sub={t.layer.miSub} />
              <Pillar code="ZekaAI" title={t.layer.zekaai} sub={t.layer.zekaaiSub} />
              <Pillar code="OnlyYou" title={t.layer.onlyyou} sub={t.layer.onlyyouSub} />
              <Pillar code="Brain" title={t.layer.brain} sub={t.layer.brainSub} />
            </div>
          </div>
          <div className="text-[11px] text-slate-500">© {APP_NAME} · v1.0</div>
        </div>

        {/* Right: form */}
        <div className="flex flex-col px-6 py-8 sm:px-12 sm:py-12">
          <div className="ml-auto">
            <LanguageSwitcher />
          </div>
          <div className="mx-auto mt-12 w-full max-w-sm">
            <h2 className="text-2xl font-semibold tracking-tight text-slate-900">
              {mode === "login" ? t.auth.login : t.auth.register}
            </h2>
            <p className="mt-1 text-sm text-slate-500">{t.auth.subtitle}</p>

            <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="login-form">
              {mode === "register" && (
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-600">
                    {t.auth.name}
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="mios-input"
                    required
                    data-testid="register-name-input"
                  />
                </div>
              )}
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">
                  {t.auth.email}
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mios-input"
                  autoComplete="email"
                  required
                  data-testid="login-email-input"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">
                  {t.auth.password}
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mios-input"
                  autoComplete={mode === "login" ? "current-password" : "new-password"}
                  required
                  minLength={8}
                  data-testid="login-password-input"
                />
              </div>
              {mode === "register" && (
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-600">
                    {t.auth.role}
                  </label>
                  <select
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    className="mios-input"
                    data-testid="register-role-select"
                  >
                    <option value="member">{t.auth.roleMember}</option>
                    <option value="clinician">{t.auth.roleClinician}</option>
                  </select>
                </div>
              )}

              {error && (
                <div
                  className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
                  data-testid="login-error"
                >
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={busy}
                className="mios-btn mios-btn-primary w-full"
                data-testid="login-submit-button"
              >
                {busy ? t.auth.submitting : mode === "login" ? t.auth.login : t.auth.register}
              </button>
            </form>

            <button
              type="button"
              onClick={() => {
                setMode(mode === "login" ? "register" : "login");
                setError("");
              }}
              className="mt-4 w-full text-center text-xs text-slate-500 hover:text-slate-900"
              data-testid="login-toggle-mode"
            >
              {mode === "login" ? t.auth.needAccount : t.auth.haveAccount}
            </button>

            <div className="mt-10 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-500">
                {t.auth.demoCredentials}
              </div>
              <div className="space-y-1 font-mono text-[11px] leading-relaxed text-slate-600">
                <div data-testid="demo-member">{t.auth.demoMember}</div>
                <div data-testid="demo-clinician">{t.auth.demoClinician}</div>
                <div data-testid="demo-admin">{t.auth.demoAdmin}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Pillar({ code, title, sub }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white/70 px-3 py-2.5 backdrop-blur">
      <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500">{code}</div>
      <div className="text-sm font-semibold tracking-tight text-slate-900">{title}</div>
      <div className="text-[11px] text-slate-500">{sub}</div>
    </div>
  );
}
